/**
 * The sections of a client record as one list beside the content, so a client's
 * service (with its own sections nested underneath) never adds a second or third
 * row of tabs. On narrow screens it becomes a single "Section" menu.
 */
import type { ComponentType } from "react";
import { cn } from "@/lib/utils";

export type ClientNavItem = {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  group?: string;
  count?: number;
  disabled?: boolean;
  title?: string;
  children?: Array<{ key: string; label: string }>;
};

export function ClientNav({ items, current, section, onSelect }: { items: ClientNavItem[]; current: string; section: string; onSelect: (tab: string, section?: string) => void }) {
  const groups: Array<{ label?: string; items: ClientNavItem[] }> = [];
  for (const it of items) {
    const last = groups[groups.length - 1];
    if (last && last.label === it.group) last.items.push(it);
    else groups.push({ label: it.group, items: [it] });
  }
  const options = items.flatMap((it) =>
    it.disabled ? [] : it.children && it.key === current ? it.children.map((ch) => ({ value: `${it.key}:${ch.key}`, label: `${it.label} › ${ch.label}` })) : [{ value: it.key, label: it.label }],
  );
  const value = items.find((i) => i.key === current)?.children ? `${current}:${section}` : current;
  return (
    <>
      <label className="flex min-w-0 items-center gap-2 text-sm lg:hidden">
        <span className="text-muted-foreground">Section</span>
        <select
          aria-label="Section"
          className="h-10 w-full min-w-0 flex-1 rounded-md border border-input bg-card px-3 text-sm font-medium"
          value={value}
          onChange={(e) => {
            const [tab, sec] = e.target.value.split(":");
            const item = items.find((i) => i.key === tab);
            onSelect(tab!, sec ?? item?.children?.[0]?.key);
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <nav aria-label="Client sections" className="hidden lg:block">
        <div className="sticky top-4 space-y-4 rounded-lg border border-card-border bg-card p-2">
          {groups.map((g, gi) => (
            <div key={gi}>
              {g.label && <p className="px-2.5 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</p>}
              <ul className="space-y-0.5">
                {g.items.map((it) => {
                  const active = it.key === current;
                  return (
                    <li key={it.key}>
                      <button
                        type="button"
                        disabled={it.disabled}
                        title={it.title}
                        aria-current={active && !it.children ? "page" : undefined}
                        onClick={() => onSelect(it.key, it.children ? (active ? section : it.children[0]!.key) : undefined)}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
                          active ? "bg-primary/10 font-semibold text-primary" : "text-foreground hover:bg-muted",
                          it.disabled && "cursor-not-allowed text-muted-foreground/60 hover:bg-transparent",
                        )}
                      >
                        <it.icon className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate">{it.label}</span>
                        {!!it.count && <span className="rounded-full bg-secondary px-1.5 text-[11px] tabular text-muted-foreground">{it.count}</span>}
                      </button>
                      {active && it.children && (
                        <ul className="mb-1 ml-[18px] mt-0.5 space-y-0.5 border-l border-border pl-2.5">
                          {it.children.map((ch) => (
                            <li key={ch.key}>
                              <button
                                type="button"
                                aria-current={section === ch.key ? "page" : undefined}
                                onClick={() => onSelect(it.key, ch.key)}
                                className={cn("w-full rounded px-2 py-1 text-left text-[13px]", section === ch.key ? "font-medium text-primary" : "text-muted-foreground hover:text-foreground")}
                              >
                                {ch.label}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}
